/* v8 ignore start */
import {Collection} from "@/lib/core/collection";
import {JsonModel} from "@/lib/core/jsonModel";
import {hash, IHashable} from "@/lib/hashable";
import {lottieAssetModelResolver, TLottieAssetJson} from "@/lib/lottie/assets/lottieAssetModelResolver";
import {LottieImageAssetModel} from "@/lib/lottie/assets/lottieImageAssetModel";
import {LottiePrecompositionModel} from "@/lib/lottie/assets/lottiePrecompositionModel";
import {TIntBoolean} from "@/lib/lottie/helpers/intBoolean";
import {LottieVisualObjectModel} from "@/lib/lottie/helpers/lottieVisualObjectModel";
import {LottiePrecompositionLayerModel} from "@/lib/lottie/layers/lottiePrecompositionLayerModel";
import {LottieFontListModel} from "@/lib/lottie/text/lottieFontListModel";

export class LottieAnimationModel extends JsonModel<LottieAnimationModel.IJson> implements IHashable {
  private _hashCode?: number;

  public get version(): string {
    return this.json.v ?? "5.9.0";
  }

  public get frameRate(): number {
    return this.json.fr;
  }

  public get inPoint(): number {
    return this.json.ip;
  }

  public get outPoint(): number {
    return this.json.op;
  }

  public get width(): number {
    return this.json.w;
  }

  public get height(): number {
    return this.json.h;
  }

  public get name(): Maybe<string> {
    return this.json.nm;
  }

  public get ddd(): Maybe<TIntBoolean> {
    return this.json.ddd;
  }

  public get is3D(): boolean {
    return this.json.ddd === 1;
  }

  public get fonts(): Maybe<LottieFontListModel.IJson> {
    return this.json.fonts;
  }

  public get duration(): number {
    return this.outPoint / this.frameRate;
  }

  public updateAssetsByType<T extends LottieImageAssetModel | LottiePrecompositionModel>(
    type: new (...args: any[]) => T,
    predicate: (assetModel: T) => T
  ): this {
    return this.update({
      assets: this.assets.map((assetModel) => {
        if (assetModel instanceof type) {
          return predicate(assetModel).toObject();
        } else {
          return assetModel.toObject();
        }
      }),
    });
  }

  public updateLayersByType<T extends LottiePrecompositionLayerModel>(
    type: new (...args: any[]) => T,
    predicate: (layerModel: T) => T
  ): this {
    return this.update({
      layers: this.layers.map((layerModel) => {
        if (layerModel instanceof type) {
          return predicate(layerModel).toObject();
        } else {
          return layerModel.toObject();
        }
      }),
    });
  }

  public addPrefixId(prefix: string): LottieAnimationModel {
    const data = this.toObject();

    function traverseLayer(layer: any) {
      if (layer.id && !layer.id.startsWith(prefix)) {
        layer.id = `${prefix}_${layer.id}`;
      }

      if (layer.refId && !layer.refId.startsWith(prefix)) {
        layer.refId = `${prefix}_${layer.refId}`;
      }

      if (layer.layers) {
        layer.layers.forEach(traverseLayer);
      }
    }

    data.layers.forEach(traverseLayer);

    if (data.assets) data.assets.forEach(traverseLayer);

    return new LottieAnimationModel(data);
  }

  public get preCompositionAssets(): Collection<LottiePrecompositionModel> {
    return this.assets.select(
      (asset) => asset instanceof LottiePrecompositionModel
    ) as Collection<LottiePrecompositionModel>;
  }

  public get assetObjects(): TLottieAssetJson[] {
    return this.json.assets;
  }

  public get layerObjects(): LottiePrecompositionLayerModel.IJson[] {
    return this.json.layers;
  }

  public get assets(): Collection<LottieImageAssetModel | LottiePrecompositionModel> {
    const {assets = []} = this.json;

    return new Collection(assets.map((assetJson) => lottieAssetModelResolver(assetJson)));
  }

  public get layers(): Collection<LottiePrecompositionLayerModel> {
    const {layers = []} = this.json;

    return new Collection(layers.map((layerJson) => new LottiePrecompositionLayerModel(layerJson)));
  }

  public hashCode(): number {
    if (this._hashCode == null) {
      this._hashCode = hash(this.json);
    }

    return this._hashCode;
  }
}

export namespace LottieAnimationModel {
  // Documentation: https://lottiefiles.github.io/lottie-docs/schema/
  // Github: https://github.com/LottieFiles/lottie-docs/blob/main/docs/schema/
  export interface IJson extends LottieVisualObjectModel.IJson {
    /**
     * Version
     * @default "5.5.2"
     */
    v?: string;

    /**
     * Framerate in frames per second
     * @default 60
     */
    fr: number;

    /**
     * "In Point", which frame the animation starts at (usually 0)
     * @default 0
     */
    ip: number;

    /**
     * "Out Point", which frame the animation stops/loops at, which makes this the duration in frames when `ip` is 0
     * @default 60
     */
    op: number;

    /**
     * Width of the animation
     * @default 512
     */
    w: number;

    /**
     * Height of the animation
     * @default 512
     */
    h: number;

    /**
     * Whether the animation has 3D layers
     * @default 0
     */
    ddd?: TIntBoolean;

    /**
     * List of assets that can be referenced by layers
     */
    assets: TLottieAssetJson[];

    /**
     * Fonts
     */
    fonts?: LottieFontListModel.IJson;

    /**
     * Markers defining named sections of the composition.
     */
    markers?: any[];

    // To make things much simpler support only precomposition layers at animation level
    // Lottie animation might have other types of layers as well: it should extend the LottieCompositionModel
    layers: LottiePrecompositionLayerModel.IJson[];
  }
}
/* v8 ignore stop */
